import React from 'react';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import useSession from 'core/hooks/use-session';
import { useQuery, gql } from '@apollo/client';
import { useStore } from 'core/store/store.mobx';
import { MyCommunity } from '../my-community';
import { Pledges } from '../pledges';
import { CustomerAdditionalOffersSection } from '../customer-additional-offers-section';
import { MicroDeposits } from '../micro-deposits';
import { AccountDetails } from '../account-details';
import { AgentDetails } from '../agent-details';
import { MarketTierCard } from '../market-tier-card';
import { checkIsFarmEnvironment } from 'common/helpers/environment-helpers';
import { isBranchEnv } from 'brand-config';
import { buildBixAutoUpsellInput } from 'common/components/branch-auto-upsell';
import useStyles from './sidebar.styles';

const isFarmEnvironment = checkIsFarmEnvironment();

const CustomerSidebar = observer((props) => {
  const {
    account,
    account: {
      policies,
      policies: { achPoliciesWithUniqueBankId },
      marketTierRating,
      accountLoading
    }
  } = useStore();
  const recentPolicy = policies.getRecentPolicy(); // Using getRecentPolicy() here as per discussion in issue #21742 - life insurance eligibility doesn't require an active policy

  const classes = useStyles();
  const { canEditPolicies, canSeeMarketTierCard, viewOnly } = useSession();

  const assurityLifeEligibilityQuery = useQuery(
    gql`
      query ($policyId: String) {
        assurityLifeCheckEligibility(args: { branchPolicyId: $policyId }) {
          __typename
          ... on AssurityLifeCheckEligibilitySuccessResponse {
            staffUserCapabilities
            eligibleLifeOffers {
              personId
              benefitAmount
              monthlyPremiumAmount
              annualPremiumAmount
            }
          }
        }
      }
    `,
    { variables: { policyId: recentPolicy?.id }, skip: !recentPolicy?.id }
  );

  const shouldShowAssurityTermLifeAdditionalOffer = Boolean(
    !assurityLifeEligibilityQuery.loading &&
      assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility.__typename !==
        'AssurityLifeCheckEligibilityStaffUserNotEligibleResponse' &&
      !isFarmEnvironment
  );

  // BIX auto upsell — only on non-Branch carrier builds when the recent policy is a
  // farm policy. Gated on `!viewOnly` so view-only operators don't trigger the
  // mount-time mutation. (We can't use `canEditPolicies` here — that's a Branch-staff
  // role check; mutual carrier agents aren't in those groups.) `isFarmBasedPolicyType`
  // covers all farm policy types (F/Z/Y/FA). Applicant name comes from the account;
  // address from `offer.quote.correctedAddress` — the post-validation policyholder
  // address attached to the originating offer, which is what BIX rates against.
  // Agent eligibility (`branch_play = "Mutuals Accommodation"`) lives server-side in
  // the create-bix-auto-offer-upsell lambda — see issue #27759.
  const bixAutoUpsellInput =
    !viewOnly && !isBranchEnv && isFarmBasedPolicyType(recentPolicy?.policyType) && recentPolicy?.id
      ? buildBixAutoUpsellInput({
          branchPolicyId: recentPolicy.id,
          applicant: { firstName: account.fname, lastName: account.lname },
          farmAddress: recentPolicy?.offer?.quote?.correctedAddress,
          email: account.email,
          phone: account.phoneNumber
        })
      : null;

  const shouldShowAdditionalOffers = shouldShowAssurityTermLifeAdditionalOffer || !!bixAutoUpsellInput;

  return (
    <Grid container>
      <Grid item className={classes.bottomSpacing}>
        <AccountDetails {...props} />
      </Grid>
      {!!marketTierRating && canSeeMarketTierCard && !isFarmEnvironment && (
        <Grid item className={classes.gridItem}>
          <MarketTierCard {...props} marketTierRating={marketTierRating} accountLoading={accountLoading} />
        </Grid>
      )}
      {canEditPolicies && !!achPoliciesWithUniqueBankId.length && account.id === props.accountId && (
        <Grid item className={classes.bottomSpacing}>
          <MicroDeposits {...props} />
        </Grid>
      )}
      <Grid item className={classes.gridItem}>
        <AgentDetails {...props} />
      </Grid>
      {shouldShowAdditionalOffers && (
        <Grid item className={classes.gridItem}>
          <CustomerAdditionalOffersSection
            assurityTermLife={shouldShowAssurityTermLifeAdditionalOffer}
            assurityTermLifeData={{
              ...assurityLifeEligibilityQuery?.data?.assurityLifeCheckEligibility,
              email: account.email
            }}
            bixAutoUpsellInput={bixAutoUpsellInput}
          />
        </Grid>
      )}
      {!isFarmEnvironment && (
        <>
          <Grid item className={classes.gridItem}>
            <MyCommunity {...props} />
          </Grid>
          <Grid item className={classes.gridItem}>
            <Pledges {...props} />
          </Grid>
        </>
      )}
    </Grid>
  );
});

export default CustomerSidebar;
